// ./components/TVChartContainer/stream.js
import _ from 'lodash';

let counter = 0;

export default function createStream(socket) {
  let _subs = [];
  let requestSeq = 0;
  const latestReqByChannel = new Map();
  const handshakeTimers = new Map();
  let currentChannel = '';
  const HISTORY_WAIT_MS = 800;

  function sendSubRequestEvent({ sub, from }) {
    currentChannel = sub;
    const req = ++requestSeq;
    latestReqByChannel.set(sub, req);

    emitSub(sub, from || 0);

    clearHandshake(sub);
    const t = setInterval(() => {
      if (latestReqByChannel.get(sub) !== req) {
        clearHandshake(sub);
        return;
      }
      emitSub(sub, from || 0);
    }, 200);
    handshakeTimers.set(sub, t);

    socket.once(`TVSub:${sub}`, () => {
      if (latestReqByChannel.get(sub) === req) {
        clearHandshake(sub);
      }
    });
  }

  function emitSub(sub, fromValue) {
    try {
      socket.emit('TVSub', { sub, fromValue });
    } catch (e) {
      console.error('stream.js emit error', e);
    }
  }

  function clearHandshake(sub) {
    const t = handshakeTimers.get(sub);
    if (t) {
      clearInterval(t);
      handshakeTimers.delete(sub);
    }
  }

  function dropChannel(channelString) {
    _subs.forEach(s => {
      if (s.channelString === channelString && s.waitHistoryTimer) {
        clearTimeout(s.waitHistoryTimer);
        s.waitHistoryTimer = null;
      }
    });
    _subs = _subs.filter(s => s.channelString !== channelString);
    clearHandshake(channelString);
    latestReqByChannel.delete(channelString);
  }

  return {
    subs: _subs,

    sendSubRequestEvent,

    subscribeBars(symbolInfo, resolution, updateCb, uid, onResetCacheNeeded) {
      const channelString = createChannelString(symbolInfo);
      //console.log(`[stream] subscribeBars: uid=${uid}, channel=${channelString}`);
      dropChannel(channelString);

      const sub = {
        channelString,
        uid,
        resolution,
        symbolInfo,
        lastBar: { time: 0 },
        listener: safeListener(updateCb),
        reset: typeof onResetCacheNeeded === 'function' ? onResetCacheNeeded : null,
        historyLoaded: false,
        waitHistoryTimer: null,
      };

      _subs.push(sub);
      sendSubRequestEvent({ sub: channelString, from: 0 });
      counter++;
    },

    unsubscribeBars(uid) {
      const idx = _subs.findIndex(e => e.uid === uid);
      if (idx === -1) return;

      const channelString = _subs[idx].channelString;
      const sub = _subs[idx];
      //console.log(`[stream] unsubscribeBars: uid=${uid}, channel=${channelString}`);

      if (sub.waitHistoryTimer) {
        clearTimeout(sub.waitHistoryTimer);
        sub.waitHistoryTimer = null;
      }

      _subs.splice(idx, 1);

      if (!_subs.some(s => s.channelString === channelString)) {
        clearHandshake(channelString);
        latestReqByChannel.delete(channelString);
      }
    },

    updateCb(params) {
      try {
        if (_.isNil(params)) return;

        let channelString = currentChannel;
        let candles = [];

        if (params && (params.exchange || params.pair || params.data)) {
          const { exchange, pair, data = [] } = params;
          if (exchange && pair) {
            channelString = createChannelString({ exchange, pair });
          }
          candles = Array.isArray(data) ? data : [];
        }

        // Find live sub
        const sub = _subs.find(e => e.channelString === channelString);
        if (!sub) return;

        // Map to bars
        const bars = sanitizeBars(candles);
        const lastStr = sub.lastBar && sub.lastBar.time ? fmtTs(sub.lastBar.time) : 'n/a';
        //console.log(`[stream] updateCb ch=${channelString} rows=${bars.length} last=${lastStr} loaded=${sub.historyLoaded}`);

        if (bars.length === 0) return;

        if (!sub.historyLoaded) {
          if (bars.length === 1) {
            if (!sub.waitHistoryTimer) {
              sub.waitHistoryTimer = setTimeout(() => {
                try {
                  const b = bars[0];
                  sub.listener(b);
                  sub.lastBar = b;
                  sub.historyLoaded = true;
                } catch (e) {
                  console.error('stream.js: error promoting single bar', e);
                } finally {
                  sub.waitHistoryTimer = null;
                }
              }, HISTORY_WAIT_MS);
            }
            return;
          }

          if (sub.waitHistoryTimer) {
            clearTimeout(sub.waitHistoryTimer);
            sub.waitHistoryTimer = null;
          }

          for (const b of bars) {
            sub.listener(b);
            sub.lastBar = b;
          }
          sub.historyLoaded = true;
          return;
        }

        // Real-time phase. Accept same-time updates to repaint the open bar.
        let forwarded = 0;
        for (const b of bars) {
          if (!sub.lastBar || b.time >= sub.lastBar.time) {
            sub.listener(b);
            sub.lastBar = b;
            forwarded++;
          }
        }
        if (forwarded) {
          //console.log(`[stream] realtime ch=${channelString} forwarded=${forwarded} lastBar=${fmtTs(sub.lastBar.time)}`);
        }
      } catch (e) {
        console.error('stream.js updateCb error', e);
      }
    },
  };
}

function createChannelString(symbolInfo) {
  const ex = symbolInfo.exchange || symbolInfo?.symbolInfo?.exchange;
  const pr = symbolInfo.pair || symbolInfo?.symbolInfo?.pair;
  return `${ex}:${pr}`;
}

function safeListener(listener) {
  return function forward(bar) {
    assertBar(bar);
    listener(bar);
  };
}

function sanitizeBars(raw) {
  if (!Array.isArray(raw)) return [];

  const mapped = [];
  for (const k of raw) {
    const t = Number(k?.[0]);
    const o = Number(k?.[1]);
    const h = Number(k?.[2]);
    const l = Number(k?.[3]);
    const c = Number(k?.[4]);
    const v = Number(k?.[5]);

    if (
      Number.isFinite(t) &&
      Number.isFinite(o) &&
      Number.isFinite(h) &&
      Number.isFinite(l) &&
      Number.isFinite(c) &&
      Number.isFinite(v)
    ) {
      mapped.push({
        time: Math.trunc(t),
        open: o,
        high: h,
        low: l,
        close: c,
        volume: v,
      });
    }
  }

  mapped.sort((a, b) => a.time - b.time);

  const deduped = [];
  let lastT = null;
  for (const b of mapped) {
    if (b.time !== lastT) {
      deduped.push(b);
      lastT = b.time;
    } else {
      deduped[deduped.length - 1] = b;
    }
  }

  return deduped;
}

function assertBar(b) {
  if (!b || !Number.isInteger(b.time)) {
    throw new Error(`bar.time must be integer milliseconds, got: ${b?.time}`);
  }
  const keys = ['open', 'high', 'low', 'close', 'volume'];
  for (const k of keys) {
    if (!Number.isFinite(b[k])) {
      throw new Error(`bar.${k} is not finite, got: ${b[k]}`);
    }
  }
}

function fmtTs(ms) {
  try {
    return new Date(ms).toISOString();
  } catch {
    return String(ms);
  }
}
